import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Link, useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { api, baixarBlob } from '../api';
import { data, dataHora, numero, plural, rotulo } from '../formato';
import { FormNovaMedicao } from './Medicoes';
import {
  QuadroDeAmostras, type AmostraDoQuadro, type AmostraEsperada,
} from './QuadroDeAmostras';
import { useSessao } from '../sessao';
import {
  Aviso, Carregando, Dialogo, ErroDaApi,
  FolhaA4, Selo, Vazio,
  SeloDoEnsaio,
} from '../ui';
import { TrilhaDaFicha } from '../Trilha';
import {
  FormAlterar, FormAmostraPrevista, FormCancelar, FormEscopo,
  FormArquivo, FormExcluirPrevista, FormPrevisaoChegada, FormProgramar, FormParada, TITULO_STATUS,
  type AmostraPrevista, type CampoStatus, type EnsaioDaPrevisao, type Os,
} from './OsForms';
import { ExcluirDeVez } from './ExclusaoDefinitiva';
import { FaixaDoEscopo, linhaDoServidor } from './FaixaDoEscopo';
import {
  AbasDaOs, CabecalhoDaOs, NOME_DA_ABA, ReguaDaOs, numerosDaOs, proximoPasso, rotaDaAba,
  type AbaDaOs,
} from './OsMoldura';
import { ResumoDaOs, type AmostraDoResumo } from './OsResumo';
import { JanelaRevisarEscopo } from './RevisarEscopo';
import {
  BotaoEtiquetas, CelulaDaEtiqueta, EtiquetaNaFicha, FaixaDeEtiquetas, JanelaEtiquetas,
  useEtiquetas,
} from './EtiquetasDaAmostra';
import { NomeDaAmostra, nomeDaAmostra } from '../NomeDaAmostra';

/**
 * A ficha da ordem de serviço.
 *
 * A OS é a peça que amarra o resto: a amostra pertence a uma, o ensaio
 * herda dela o cliente e a obra, o relatório sai no nome dela e a medição
 * mede o que ela contratou. Esta tela é onde essas linhas se encontram.
 *
 * Ela é organizada pela ordem das perguntas de quem abre a OS no meio do
 * serviço, e não pela ordem das colunas no banco:
 *
 *   1. **está atrasada?** — antes de qualquer outra coisa;
 *   2. **em que pé estão as três frentes?** — técnico, relatório e medição
 *      são independentes, e é por isso que aparecem lado a lado em vez de
 *      resumidos num status só;
 *   3. **o que foi programado e o que já chegou** — numa tabela só, desde
 *      a onda 204: cada amostra prevista é uma linha, e a linha ganha o
 *      código quando o material chega. Furos e diários de campo saíram
 *      daqui para `/os/:id/campo` (o módulo de campo vai assumi-los);
 *   4. **quem responde e quais são os prazos** — só o responsável
 *      técnico, que é do admin (204);
 *   5. **o que aconteceu até aqui** — o histórico, que é gravado por
 *      gatilho do banco e não por esta tela.
 */

/* Os tons das três frentes moram em `OsResumo.tsx` desde a 280 (a faixa de
   situação); a ficha não os usa mais. */

/** Uma revisão de escopo, como `vw_os_revisao` a entrega (0041). */
interface Revisao {
  id: string;
  numero: number | string;
  motivo: string;
  solicitante: string | null;
  criado_em: string;
  criado_por_nome: string | null;
  /** 283: o que a revisão mudou no escopo (antes/depois por item). */
  mudancas?: Array<{ codigo: string; antes: string | number | null; depois: string | number | null }>;
}

/** «+ TRI.CIU 1», «UMID 10 → 8», «CBR sai» (283). */
function dizMudanca(m: { codigo: string; antes: string | number | null; depois: string | number | null }) {
  const a = m.antes === null ? null : Number(m.antes);
  const d = m.depois === null ? null : Number(m.depois);
  if (a === null && d !== null) return `+ ${m.codigo} ${numero(d, 0, true)}`;
  if (a !== null && d === null) return `${m.codigo} sai`;
  return `${m.codigo} ${numero(a ?? 0, 0, true)} → ${numero(d ?? 0, 0, true)}`;
}

interface Evento {
  campo: string; de: string | null; para: string;
  datahora: string; motivo: string | null;
  usuario_nome: string | null; usuario_email: string | null;
}

type Form =
  | { que: 'parada'; acao: 'suspender' | 'retomar' }
  | { que: 'alterar' }
  | { que: 'cancelar' }
  /* 0288: arquivar ou desarquivar */
  | { que: 'arquivo' }
  | { que: 'amostra' }
  | { que: 'prever-serie' }
  | { que: 'revisar'; adicionar?: boolean }
  | { que: 'escopo' }
  | { que: 'medicao' }
  /* 227: a amostra por chegar que nunca vai chegar */
  | { que: 'excluir-prevista'; id: string; nome: string | null; ensaios: number }
  /* 240 (D5): quando as amostras por chegar devem chegar */
  | { que: 'previsao' };

export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `/os/:id` aceita também o NÚMERO da OS (244).
 *
 * As listas de recebimentos, de amostras, do descarte e da assinatura
 * montavam o link com o número («/os/2609353») e a API, que só aceita o
 * id, respondia «Não existe registro com este identificador.» — o fluxo
 * quebrava exatamente na passagem de uma tela para a outra. Os links que
 * têm o id passam a usar o id; para os demais, e para quem digita o
 * número no endereço ou guardou um favorito, a ficha acha a OS pelo número
 * e troca o endereço pelo do id, sem deixar a etapa no histórico.
 */
export function OsTela({ aba = 'resumo' }: { aba?: AbaDaOs }) {
  const { id = '' } = useParams();
  if (!UUID.test(id)) return <OsPeloNumero numero={id} aba={aba} />;
  return <FichaDaOs aba={aba} />;
}

export function OsPeloNumero({ numero, aba }: { numero: string; aba: AbaDaOs }) {
  const navegar = useNavigate();
  const { search, hash } = useLocation();
  const [naoAchou, setNaoAchou] = useState(false);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    let viva = true;
    setNaoAchou(false); setErro(null);
    api<{ id: string; numero: string | number }[]>(
      `/os?busca=${encodeURIComponent(numero)}`)
      .then((lista) => {
        if (!viva) return;
        const achada = lista.find((o) => String(o.numero) === numero.trim());
        if (achada) navegar(`${rotaDaAba(achada.id, aba)}${search}${hash}`, { replace: true });
        else setNaoAchou(true);
      })
      .catch((e) => { if (viva) setErro(e); });
    return () => { viva = false; };
  }, [numero, aba, navegar, search, hash]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (naoAchou) {
    return (
      <Aviso tipo="erro" titulo={`Não existe OS com o número ${numero}.`}>
        Confira o número ou procure na <Link to="/os">lista de OS</Link>.
      </Aviso>
    );
  }
  return <Carregando texto={`Procurando a OS ${numero}`} />;
}

function FichaDaOs({ aba }: { aba: AbaDaOs }) {
  const { id = '' } = useParams();
  const { pode } = useSessao();
  const [os, setOs] = useState<Os | null>(null);
  const [previstas, setPrevistas] = useState<AmostraPrevista[] | null>(null);
  const [recebidas, setRecebidas] = useState<AmostraDoQuadro[] | null>(null);
  const [ensaiosDe, setEnsaiosDe] = useState<Map<string, EnsaioDaAmostra[]>>(new Map());
  const [historico, setHistorico] = useState<Evento[] | null>(null);
  const [revisoes, setRevisoes] = useState<Revisao[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [form, setForm] = useState<Form | null>(null);
  /* 283: a revisão recém-gravada ganha a faixa verde na aba Escopo (o
     hook mora aqui em cima: depois dos `return` de carregando, quebraria
     a ordem dos hooks). */
  const [revisada, setRevisada] = useState<number | null>(null);
  /* 287: a situação das etiquetas das recebidas, as linhas marcadas e a
     janela «Imprimir etiquetas» (aqui em cima pelo mesmo motivo). */
  /*
   * H1 (onda 295): a OS é Comum (D1) e abre também no Hub, para quem não tem
   * o LabManager — o Gestor de campo, o Comercial e medição. As amostras
   * (`amostra.ver`) e as etiquetas delas são do LabManager: a API recusa com
   * «Você não tem acesso ao LabManager.», e a recusa de UMA parte não pode
   * trocar a ficha inteira pelo erro. Sem a unidade, nem se pergunta — a
   * lista fica vazia, como na busca global.
   */
  const veAmostras = pode('amostra.ver');
  const etiquetas = useEtiquetas({
    os: veAmostras && (aba === 'amostras' || aba === 'resumo') ? id : null,
  });
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [janelaEtq, setJanelaEtq] = useState(false);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const [ficha, prev, hist, revs, amostras, ensaios] = await Promise.all([
        api<Os>(`/os/${id}`),
        api<AmostraPrevista[]>(`/os/${id}/amostras-previstas`),
        api<Evento[]>(`/os/${id}/historico`),
        api<Revisao[]>(`/os/${id}/revisoes`),
        /*
         * As amostras que JÁ CHEGARAM — não as previstas.
         *
         * «Isso pode ser editado em Amostras ou em OS» (Humberto,
         * 21/09/2026): a ficha da OS não tinha onde corrigir a ficha de
         * uma amostra, porque não mostrava as amostras recebidas. Passa
         * a mostrar, com a mesma tabela do recebimento.
         */
        veAmostras
          ? api<AmostraDoQuadro[]>(`/amostras?os=${id}&ordem=col_amostra&sentido=asc`)
          : Promise.resolve<AmostraDoQuadro[]>([]),
        /* Os ensaios reais de cada amostra recebida — os chips da tabela
           única (204). A previsão só sabe o que foi MARCADO antes de a
           amostra chegar; depois, o que vale é o ensaio. */
        api<EnsaioDaAmostra[]>(`/os/${id}/ensaios-por-amostra`),
      ]);
      setOs(ficha);
      setPrevistas(prev);
      setRecebidas(amostras);
      const mapa = new Map<string, EnsaioDaAmostra[]>();
      for (const e of ensaios) {
        const lista = mapa.get(e.amostra_id) ?? [];
        lista.push(e);
        mapa.set(e.amostra_id, lista);
      }
      setEnsaiosDe(mapa);
      setHistorico(hist);
      setRevisoes(revs);
    } catch (e) { setErro(e); }
  }, [id, veAmostras]);

  useEffect(() => { void carregar(); }, [carregar]);

  /*
   * `?revisar=1` abre o diálogo de revisão (UX-058).
   *
   * O link «abrir uma revisão da OS» — na programação e na situação do
   * ensaio — apontava para cá com esse parâmetro desde a 0074, e a ficha
   * nunca o leu: quem clicava recarregava a ficha e procurava o botão.
   * Lido uma vez, e apagado do endereço para o F5 não reabrir o diálogo.
   */
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!os) return;
    const revisar = params.get('revisar') === '1';
    /*
     * `?escopo=1` (0220, RN-218): «Incluir na OS», vindo da programação
     * ou da recusa do escopo. A ficha escolhe o diálogo: enquanto nada
     * foi programado, o escopo muda livre; depois, é a revisão.
     */
    const escopo = params.get('escopo') === '1';
    if (!revisar && !escopo) return;
    if (!os.cancelada) {
      /* 283: OS sem escopo lança; com escopo, a janela única — já com o
         catálogo aberto quando vem do «Incluir na OS». */
      const temItens = (os.escopo ?? []).length > 0;
      const podeMudar = os.escopo_livre ? pode('os.editar') : pode('os.revisar');
      if (escopo && !temItens && pode('os.editar')) setForm({ que: 'escopo' });
      else if (podeMudar) setForm({ que: 'revisar', adicionar: escopo });
    }
    const q = new URLSearchParams(params);
    q.delete('revisar');
    q.delete('escopo');
    setParams(q, { replace: true });
  }, [os, params, pode, setParams]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!os) return <Carregando texto="Carregando a ordem de serviço" />;

  const fechar = () => { setForm(null); void carregar(); };
  const total = Number(os.amostras_previstas_total);
  const atendidas = Number(os.amostras_previstas_atendidas);
  const cancelada = Boolean(os.cancelada);
  const suspensa  = Boolean(os.suspensa);

  const passo = proximoPasso(os, pode);

  /* RN-f (0220): o que a previsão marcou e não entrou na chegada. Vale no
     Resumo (é pendência) e no Escopo (é onde se resolve). */
  const naoEntrou = (os.previsto_nao_entrou ?? []).length > 0 && (
    <Aviso tipo="atencao"
      titulo={`${os.previsto_nao_entrou!.length} ensaio(s) previsto(s) não entraram quando a amostra chegou`}>
      <p style={{ margin: '0 0 .4rem' }}>
        {os.previsto_nao_entrou!.map((x) =>
          // 303 (RN-248): a amostra pelo nome do cliente, com o código.
          `${x.metodo_codigo} na ${nomeDaAmostra({
            codigo: x.amostra_codigo, identificacao: x.identificacao_prevista })}`).join(' · ')}
      </p>
      <p className="sub" style={{ margin: 0 }}>
        O escopo não tinha mais lugar para eles, ou o método saiu do escopo
        depois de marcado. Se o ensaio ainda vale, inclua-o na OS e programe
        de novo{' '}
        <Link to={`/os/${os.id}/programacao`}>na programação</Link>.
      </p>
    </Aviso>
  );

  return (
    <div className="ficha-os">
      {/*
        * 256 · a OS em abas. O cabeçalho, a régua e as abas são os mesmos
        * em todas as abas; o conteúdo muda com o endereço. A trilha
        * («Ordens de serviço / OS n / Escopo») é da moldura, logo acima.
        */}
      <CabecalhoDaOs os={os} passo={passo} acoes={<AcoesDaOs os={os} />} />
      {/* 0288 (RN-279): a arquivada diz que está fora das filas, e por quê. */}
      {os.arquivada && (
        <Aviso tipo="info"
          titulo={`OS arquivada em ${data(os.arquivada_em)}${
            os.arquivada_por_nome ? ` por ${os.arquivada_por_nome}` : ''}`}>
          <p style={{ margin: '0 0 .4rem' }}>
            {os.arquivada_motivo ? `«${os.arquivada_motivo}». ` : ''}
            Fora da lista de OS, da fila, do painel e do cronograma, e só
            leitura: não recebe amostra, ensaio nem lançamento.
          </p>
          {pode('os.arquivar') && (
            <button type="button" className="botao secundario"
              onClick={() => setForm({ que: 'arquivo' })}>
              Desarquivar esta OS
            </button>
          )}
        </Aviso>
      )}
      {/*
        A identidade da OS tem três partes desde a 0041: o lote diz QUAL
        remessa deste projeto, e a revisão (no selo do cabeçalho) diz QUAL
        versão do escopo.
      */}
      {Number(os.lotes_irmaos) > 0 && (
        <p className="meta lotes-irmaos">
          Esta obra tem {String(Number(os.lotes_irmaos) + 1)} lotes —{' '}
          <Link to={`/os?obra=${os.obra_id}`}>ver todos</Link>
        </p>
      )}
      <ReguaDaOs os={os} aba={aba} semMedicao={!pode('medicao.ver')}
        semAmostras={!veAmostras} semProgramacao={!pode('ensaio.ver')} />
      <AbasDaOs os={os} aba={aba} semQuantitativos={!pode('medicao.ver')}
        semAmostras={!veAmostras} semProgramacao={!pode('ensaio.ver')} />
      <h2 className="so-leitor">{NOME_DA_ABA[aba]}</h2>

      {/* 280 · o Resumo da OS no lugar do papel: a faixa de situação, o
         escopo e andamento, a grade, as instruções, os recebimentos e os
         Dados da OS — tudo em `OsResumo.tsx`, desenhado mais abaixo. */}

      {/*
        * ── 3. O escopo contratado, ANTES da programação ───────────────
        *
        * «Nas OS é importante separar o que é cadastro de ensaios do que
        *  é programar ensaios. Hoje está confuso. Ao abrir a OS
        *  normalmente a informação que temos é a quantidade de cada
        *  ensaio. Não necessariamente saberemos a programação de ensaios
        *  para cada amostra» — Testesolo, 02/09/2026.
        *
        * O escopo era o bloco 4, embaixo de «Amostras previstas», dos
        * furos e dos diários de campo — quinto numa página que rola.
        * Quem abria a OS recém-criada via primeiro a tela pedindo o que
        * ele ainda não sabe (qual ensaio em qual amostra) e por último a
        * única coisa que ele sabe (quantos ensaios de cada tipo foram
        * contratados). A ordem estava invertida, e era isso o «confuso».
        *
        * Agora: quantidades primeiro; amarração depois.
        */}

      {/* ── 4. Escopo contratado ──────────────────────────────────── */}

      {aba === 'escopo' && (<>
      <Escopo os={os} revisoes={revisoes} revisada={revisada}
        podeRevisar={(os.escopo_livre ? pode('os.editar') : pode('os.revisar')) && !cancelada}
        podeLancar={pode('os.editar') && !cancelada}
        aoRevisar={() => setForm({ que: 'revisar' })}
        aoLancar={() => setForm({ que: 'escopo' })} />

      {naoEntrou}
      </>)}

      {/* ── 3b. Programação de amostras e ensaios — a tabela única ── */}

      {/*
        * «A ideia seria unificar essas 2 tabelas em uma só para
        *  simplificar a conferência» — Humberto, 22/09/2026. Eram duas:
        * a das PREVISTAS (o que a OS espera) e, embaixo, a das RECEBIDAS
        * (o `QuadroDeAmostras` da 196). Quem conferia lia a mesma amostra
        * duas vezes, em duas linhas, com colunas diferentes.
        *
        * Agora é uma: cada previsão é uma linha; quando a amostra chega, a
        * linha ganha o código, a situação e a Edição rápida. O que chegou
        * sem ter sido previsto entra no fim. A ordem é a da previsão —
        * não a do lote (194), porque uma OS pode ter amostras de vários
        * lotes e esta tela nem diz qual lote é.
        */}
      {aba === 'amostras' && (previstas === null || recebidas === null ? <Carregando /> : (() => {
        const ordemDe = new Map<string, number>();
        for (const p of previstas) {
          if (p.amostra_id) ordemDe.set(p.amostra_id, p.ordem);
        }
        const esperadas: AmostraEsperada[] = previstas
          .filter((p) => !p.atendida)
          .map((p) => ({
            id: p.id, ordem: p.ordem,
            identificacao_prevista: p.identificacao_prevista,
            origem_prevista: p.origem_prevista,
            observacoes: p.observacoes,
            tipo_amostra: p.tipo_amostra ?? null,
            profundidade_inicial: p.profundidade_inicial ?? null,
            profundidade_final: p.profundidade_final ?? null,
            latitude: p.latitude, longitude: p.longitude,
            utm_e: p.utm_e, utm_n: p.utm_n, utm_fuso: p.utm_fuso,
            origem_coordenada: p.origem_coordenada,
            previsao_chegada: p.previsao_chegada ?? null,
            previsao_origem: p.previsao_origem ?? null,
            chegada_atrasada_dias: p.chegada_atrasada_dias ?? null,
          }));
        const chipsDaPrevisao = new Map<string, EnsaioDaPrevisao[]>();
        for (const p of previstas) chipsDaPrevisao.set(p.id, p.ensaios_codigos ?? []);
        const previsaoDe = new Map<string, string>();
        for (const p of previstas) if (p.amostra_id) previsaoDe.set(p.amostra_id, p.id);

        const linhas = recebidas.map((a) => ({ ...a, ordem: ordemDe.get(a.id) ?? null }));
        const faltam = total - atendidas;

        return (<>
          <QuadroDeAmostras
            amostras={linhas}
            esperadas={esperadas}
            aoExcluirEsperada={pode('os.programar') && !cancelada
              ? (e) => setForm({ que: 'excluir-prevista', id: e.id,
                nome: e.identificacao_prevista,
                ensaios: (chipsDaPrevisao.get(e.id) ?? []).length })
              : undefined}
            lixeira
            colunaCoordenada
            edicaoDasEsperadas={pode('os.programar') && !cancelada ? os.id : undefined}
            ordenarNaTela
            colunaOrdem
            colunaTipo
            colunaEnsaios={{
              recebida: (a) => {
                const reais = ensaiosDe.get(a.id);
                if (reais && reais.length) {
                  return (
                    <ChipsDeEnsaios itens={reais.map((e) => ({
                      chave: e.id, codigo: e.codigo, escopo: e.escopo,
                      titulo: `${e.nome} · ${rotulo(e.status)}`,
                      feito: e.status === 'aprovado',
                    }))} />
                  );
                }
                const prev = previsaoDe.get(a.id);
                const marcados = prev ? chipsDaPrevisao.get(prev) ?? [] : [];
                return <ChipsDaPrevisao itens={marcados} />;
              },
              esperando: (e) => (
                <ChipsDaPrevisao itens={chipsDaPrevisao.get(e.id) ?? []} />
              ),
            }}
            podeEditar={pode('amostra.movimentar') && !cancelada}
            aoSalvo={async () => { await carregar(); await etiquetas.recarregar(); }}
            selecao={{
              marcadas,
              aoMarcar: (aid, sim) => setMarcadas((m) => {
                const n = new Set(m);
                if (sim) n.add(aid); else n.delete(aid);
                return n;
              }),
              aoMarcarTodas: (sim) => setMarcadas(sim ? new Set(recebidas.map((a) => a.id)) : new Set()),
            }}
            colunaEtiqueta={(a) => <CelulaDaEtiqueta e={etiquetas.porAmostra.get(a.id)} />}
            fichaExtra={(a) => (
              <EtiquetaNaFicha e={etiquetas.porAmostra.get(a.id)} amostraId={a.id}
                aoImpresso={() => { void etiquetas.recarregar(); }} />
            )}
            titulo={(
              <>
                Programação de Amostras e Ensaios
                <span className="sub" style={{ marginLeft: '.6rem', fontWeight: 400 }}>
                  {total === 0
                    ? (recebidas.length > 0
                      ? `${recebidas.length} recebida(s)` : 'nenhuma amostra')
                    : `${atendidas} de ${total} já chegaram`}
                  {Number(os.ensaios_total) > 0 && ` · ${os.ensaios_total} ensaio(s)`}
                </span>
              </>
            )}
            acoes={(<>
              {recebidas.length > 0 && (
                <BotaoEtiquetas etiquetas={etiquetas.dados} aoAbrir={() => setJanelaEtq(true)} />
              )}
              {pode('os.programar') && !cancelada && (
              <>
                {faltam > 0 && (
                  <button type="button" className="botao secundario"
                    onClick={() => setForm({ que: 'previsao' })}>
                    Previsão de chegada
                  </button>
                )}
                <Link className="botao" to={`/os/${os.id}/programacao`}>
                  Programar
                </Link>
              </>
              )}
            </>)}
            nota={(<>
              <FaixaDeEtiquetas osId={os.id} etiquetas={etiquetas.dados}
                aoImpresso={() => { void etiquetas.recarregar(); }} />
              {faltam > 0 && (
                <Aviso tipo="atencao" titulo={`Faltam chegar ${faltam} amostra(s).`}>
                  Quando chegarem, registre o recebimento — os ensaios marcados
                  nelas nascem programados.
                </Aviso>
              )}
            </>)}
            vazio={(
              <div className="cartao">
                <div className="cartao-cabeca">
                  <h2>Programação de Amostras e Ensaios</h2>
                  {pode('os.programar') && !cancelada && (
                    <Link className="botao" to={`/os/${os.id}/programacao`}>
                      Programar
                    </Link>
                  )}
                </div>
                <Vazio>
                  Nenhuma amostra ainda. Em «Programar» você diz quantas vão
                  chegar, de onde, e quais ensaios cada uma leva — antes ou
                  depois de o material chegar.
                </Vazio>
              </div>
            )}
          />
          {/* 287: «Imprimir etiquetas» — a OS como lugar principal (RN-232). */}
          <JanelaEtiquetas aberto={janelaEtq} osId={os.id} osNumero={os.numero}
            etiquetas={etiquetas.dados}
            marcadas={recebidas.filter((a) => marcadas.has(a.id)).map((a) => a.id)}
            aoFechar={() => setJanelaEtq(false)}
            aoImpresso={() => { void etiquetas.recarregar(); }} />
        </>);
      })())}

      {/*
        * Furos, verticais, pontos e diários de campo moravam aqui (3b/3c).
        * Desde a 204 estão em /os/:id/campo — o link fica no cabeçalho.
        */}

      {/* ── 4b. O que já foi executado ────────────────────────────── */}

      {aba === 'quantitativos' && pode('medicao.ver') && (
        <Quantitativos osId={id}
          podeMedir={pode('medicao.elaborar') && !cancelada}
          aoMedir={() => setForm({ que: 'medicao' })} />
      )}

      {aba === 'resumo' && (previstas === null || recebidas === null ? <Carregando /> : (
        <ResumoDaOs os={os} previstas={previstas} recebidas={recebidas as AmostraDoResumo[]}
          aoPrevisao={() => setForm({ que: 'previsao' })}
          aoMudar={carregar}
          etiquetaDe={(amostraId) => (
            <EtiquetaNaFicha e={etiquetas.porAmostra.get(amostraId)} amostraId={amostraId}
              aoImpresso={() => { void etiquetas.recarregar(); }} />
          )}
          aviso={<>
            {cancelada && (
              <Aviso tipo="atencao" titulo="Esta OS está cancelada.">
                Ela continua existindo, com tudo o que aconteceu nela. Nenhum
                serviço novo deve ser lançado aqui.
              </Aviso>
            )}
            {naoEntrou}
          </>}
          dados={(<>
          {/* ── 5. Prazos, responsáveis, contrato ─────────────────────── */}

          <div className="cartao">
            <div className="cartao-cabeca">
              <h2>Dados da OS</h2>
              {pode('os.editar') && !cancelada && (
                <button type="button" className="botao secundario"
                  onClick={() => setForm({ que: 'alterar' })}>
                  Editar
                </button>
              )}
            </div>

            <div className="rola">
              <table className="dados">
                <tbody>
                  <Linha rot="Obra">
                    <span className="mono">{os.obra_codigo}</span> · {os.obra_nome}
                  </Linha>
                  <Linha rot="Cliente">{os.cliente}</Linha>
                  <Linha rot="Proposta / contrato">
                    {os.proposta_numero ?? <span className="nada">não informada</span>}
                  </Linha>
                  <Linha rot="Prioridade">{rotulo(os.prioridade)}</Linha>
                  <Linha rot="Prazo contratual">
                    {os.prazo_contratual
                      ? data(os.prazo_contratual)
                      : <span className="nada">sem prazo</span>}
                  </Linha>
                  <Linha rot="Previsto">
                    {os.data_prevista_inicio || os.data_prevista_fim
                      ? `${data(os.data_prevista_inicio) || '—'} a ${data(os.data_prevista_fim) || '—'}`
                      : <span className="nada">não definido</span>}
                  </Linha>
                  <Linha rot="Realizado">
                    {os.data_realizada_inicio || os.data_realizada_fim
                      ? `${data(os.data_realizada_inicio) || '—'} a ${data(os.data_realizada_fim) || '—'}`
                      : <span className="nada">não iniciado</span>}
                  </Linha>
                  <Linha rot="Guarda das amostras">
                    {numero(os.prazo_guarda_dias, 0)} dia(s) após o laudo aprovado
                  </Linha>
                  {/*
                    * Gestor e Responsável comercial saíram (204): «deixe apenas
                    * responsável técnico, que poderá ser definido apenas pelos
                    * Administradores do sistema». O RT se troca em «Editar»,
                    * e só aparece o seletor para quem tem a permissão.
                    */}
                  {/* Quem abriu (225): da trilha, nunca digitado. */}
                  <Linha rot="Aberta por">
                    {os.aberta_por_nome ?? <span className="nada">não registrado</span>}
                  </Linha>
                  <Linha rot="Responsável técnico">
                    {os.responsavel_tecnico_nome
                      ?? <span className="nada">não definido</span>}
                    {!os.responsavel_tecnico_nome && !pode('os.definir_responsavel_tecnico') && (
                      <span className="sub" style={{ marginLeft: '.4rem' }}>
                        — o administrador define
                      </span>
                    )}
                  </Linha>
                  <Linha rot="Aberta em">{dataHora(os.criado_em)}</Linha>
                </tbody>
              </table>
            </div>

            {os.observacoes && (
              <p className="sub" style={{ marginTop: '.6rem', whiteSpace: 'pre-wrap' }}>
                {os.observacoes}
              </p>
            )}

            {/*
              Parar a OS.

              Desde a 0059 a situação se deriva do serviço. Sobrou aqui o que
              nenhum fato consegue provar: nenhum ensaio diz que a obra parou.
              Por isso as duas ações moram juntas e longe das três caixas — o
              técnico que vem "mudar a situação" tem que encontrar ESTAS, e não
              um seletor com treze estados.
            */}
            {!cancelada && !os.arquivada && (pode('os.status') || pode('os.cancelar')) && (
              <p className="rodape-regra">
                {pode('os.status') && (
                  <button type="button" className="botao discreto"
                    onClick={() => setForm({
                      que: 'parada', acao: suspensa ? 'retomar' : 'suspender',
                    })}>
                    {suspensa ? 'Retomar esta OS' : 'Suspender esta OS'}
                  </button>
                )}
                {pode('os.cancelar') && (
                  <button type="button" className="botao discreto"
                    onClick={() => setForm({ que: 'cancelar' })}>
                    Cancelar esta OS
                  </button>
                )}
                A situação não se digita: ela vem do andamento do serviço. Estas
                duas são as únicas paradas que o serviço não consegue explicar
                sozinho.
              </p>
            )}
            {/* 0288: tirar do caminho, sem apagar — também a cancelada. */}
            {!os.arquivada && pode('os.arquivar') && (
              <p className="rodape-regra">
                <button type="button" className="botao discreto"
                  onClick={() => setForm({ que: 'arquivo' })}>
                  Arquivar esta OS
                </button>
                Arquivar tira a OS da lista, das filas e do painel, sem apagar
                nada — para a OS cujo relatório saiu fora do sistema.
              </p>
            )}
          </div>

          </>)} />
      ))}

      {/* ── 6. O que aconteceu até aqui ───────────────────────────── */}

      {aba === 'historico' && (<>
      <Historico linhas={historico} />

      {/*
        * 6b. A trilha da OS em si (UX-010). O histórico acima é o dos
        * STATUS; este é o das alterações na ficha — prazo, prioridade,
        * responsáveis — com quem, quando, o valor antigo e o motivo.
        * `fechar` recarrega tudo depois de gravar; a chave muda para a
        * trilha recarregar junto.
        */}
      <TrilhaDaFicha
        key={`trilha-${os.revisao}-${os.prioridade}-${os.prazo_contratual ?? ''}-${os.observacoes ?? ''}`}
        rota={`/os/${os.id}/trilha`}
        titulo="Trilha de alterações da OS"
        explica="Quem mudou o quê nesta ficha, com o valor anterior, o novo e o motivo informado. Não se apaga nem se edita."
      />
      </>)}

      {form?.que === 'parada' && (
        <FormParada os={os} acao={form.acao}
          aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'alterar' && (
        <FormAlterar os={os} aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'arquivo' && (
        <FormArquivo os={os} aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'cancelar' && (
        <FormCancelar os={os} aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'amostra' && (
        <FormAmostraPrevista os={os} aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'prever-serie' && (
        <FormProgramar os={os} aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'medicao' && (
        <FormNovaMedicao osId={id} obraId={String(os.obra_id)}
          aoFechar={() => setForm(null)} />
      )}
      {form?.que === 'escopo' && (
        <FormEscopo os={os} aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'revisar' && (
        <JanelaRevisarEscopo os={os} adicionar={form.adicionar}
          aoFechar={() => setForm(null)}
          aoPronto={(n) => { setRevisada(n); fechar(); }} />
      )}
      {form?.que === 'previsao' && previstas && (
        <FormPrevisaoChegada osId={id!} previstas={previstas}
          aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
      {form?.que === 'excluir-prevista' && (
        <FormExcluirPrevista osId={id!} prevista={form}
          aoFechar={() => setForm(null)} aoPronto={fechar} />
      )}
    </div>
  );
}

/** Um ensaio real de uma amostra recebida (`GET /os/:id/ensaios-por-amostra`). */
interface EnsaioDaAmostra {
  id: string;
  amostra_id: string;
  codigo: string;
  nome: string;
  escopo: string | null;
  status: string;
}

/**
 * Os chips de ensaio de uma linha da tabela única — o mesmo cartão
 * colorido pelo escopo (0162) para a previsão e para o ensaio real.
 */
function ChipsDeEnsaios({ itens }: {
  itens: Array<{
    chave: string; codigo: string; escopo: string | null;
    titulo?: string; bateria?: boolean; feito?: boolean;
  }>;
}) {
  if (!itens.length) return <span className="nada">nenhum marcado</span>;
  return (
    <span className="mini-tags ensaios-previstos">
      {itens.map((e) => (
        <span key={e.chave}
          className={`mini escopo-${e.escopo ?? 'outros'}${e.bateria ? ' bateria' : ''}`}
          title={e.titulo}>
          {e.codigo}{e.feito ? ' ✓' : ''}
        </span>
      ))}
    </span>
  );
}

function ChipsDaPrevisao({ itens }: { itens: EnsaioDaPrevisao[] }) {
  return (
    <ChipsDeEnsaios itens={itens.map((e) => ({
      chave: e.codigo, codigo: e.codigo, escopo: e.escopo, bateria: e.bateria,
      titulo: e.bateria ? `${e.codigo}: ${e.codigos?.join(' + ')}` : undefined,
    }))} />
  );
}

const Linha = ({ rot, children }: { rot: string; children: React.ReactNode }) => (
  <tr><th scope="row">{rot}</th><td>{children}</td></tr>
);

/**
 * O escopo contratado — hoje, só leitura.
 *
 * Não é esquecimento e vale dizer por quê na própria tela: um item de
 * escopo aponta para um `item_medicao`, e o catálogo de itens de medição
 * **está vazio e não tem cadastro em lugar nenhum** — nem tela, nem rota
 * de API. Oferecer um botão "acrescentar item" que abre uma lista vazia
 * seria o mesmo beco que a tela de recebimento tinha com o cliente.
 *
 * O caminho honesto é este: mostrar o que existe, dizer que o cadastro do
 * catálogo ainda não chegou, e não fingir um formulário.
 */
function Escopo(
  { os, revisoes, revisada, podeRevisar, podeLancar, aoRevisar, aoLancar }:
  {
    os: Os; revisoes: Revisao[];
    /** 283: o número da revisão que esta tela acabou de gravar. */
    revisada: number | null;
    podeRevisar: boolean; podeLancar: boolean;
    aoRevisar: () => void; aoLancar: () => void;
  },
) {
  const itens = os.escopo ?? [];
  const porMetodo = os.escopo_por_metodo ?? [];
  const resumo = os.escopo_resumo ?? null;
  const conta = numerosDaOs(os);
  const rev = Number(os.revisao);
  /* 0220 (RN-218, D2): antes do primeiro ensaio, o escopo muda livre. */
  const livre = os.escopo_livre !== false;
  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Escopo contratado</h2>
        <span className="sub">
          {/* 332 (D1): a mesma conta da régua — a marca por chegar conta. */}
          {resumo && resumo.ensaios_contratados > 0
            ? `${conta.programadosTodos} de `
              + plural(conta.contratados, 'ensaio programado', 'ensaios programados')
              + (conta.reservados > 0 ? ` · ${conta.reservados} por chegar` : '')
            : `${itens.length} item(ns)`}
          {rev > 0 && ` · revisão ${rev}`}
        </span>
        {/*
          * 283 · um botão só («não seriam necessários 2 botões» — Humberto,
          * 06/10/2026). Sem escopo, lança; com escopo, «Revisar escopo»
          * abre a janela que inclui ensaio novo, aumenta, reduz e retira.
          */}
        {itens.length === 0 ? (podeLancar && (
          <button type="button" className="botao secundario" onClick={aoLancar}>
            Lançar o escopo
          </button>
        )) : (podeRevisar && (
          <button type="button" className="botao secundario revisar-escopo-botao"
            title={livre
              ? 'Incluir ensaio, mudar a quantidade ou tirar do escopo — sem revisão: nada foi programado ainda'
              : 'Incluir ensaio, mudar a quantidade ou tirar do escopo — abre uma revisão da OS (RN-218)'}
            onClick={aoRevisar}>
            Revisar escopo
          </button>
        ))}
      </div>

      {/* 283: a revisão recém-gravada, com o que mudou. */}
      {revisada !== null && rev === revisada && revisoes[0] && Number(revisoes[0].numero) === revisada && (
        <div className="revisao-gravada" role="status">
          <b>✓ Revisão {revisada} gravada</b>
          <span className="sub">
            {dataHora(revisoes[0].criado_em)}
            {revisoes[0].criado_por_nome && ` · ${revisoes[0].criado_por_nome}`}
            {revisoes[0].solicitante && ` · pedido por ${revisoes[0].solicitante}`}
            {/* O PDF continua no menu «⋯» do cabeçalho (Humberto, 06/10). */}
            {' · o PDF da OS (menu ⋯) sai em R'}{String(revisada).padStart(2, '0')}
          </span>
          {(revisoes[0].mudancas ?? []).length > 0 && (
            <span className="revisao-mudancas">
              {(revisoes[0].mudancas ?? []).map((m) => (
                <span key={m.codigo} className="chip-mudanca mono">{dizMudanca(m)}</span>
              ))}
            </span>
          )}
        </div>
      )}

      {/*
        * D1 (0220): OS sem ensaio no escopo não programa. A frase diz o
        * porquê e o que fazer, no lugar onde se faz.
        */}
      {porMetodo.length === 0 && !os.cancelada && (
        <p className="sub aviso-linha">
          ▲ Esta OS ainda não tem ensaio no escopo — e é o escopo que libera a
          programação (RN-218). Lance os ensaios contratados antes de programar.
        </p>
      )}
      {!livre && porMetodo.length > 0 && (
        <p className="sub">
          A programação já começou: incluir ensaio, aumentar ou reduzir agora é
          revisão da OS («Revisar escopo»). A quantidade não desce abaixo do que
          já está em uso (RN-229); marcado em amostra por chegar já desconta do
          saldo.
        </p>
      )}

      {/*
        * O escopo POR MÉTODO, antes da lista de itens comerciais.
        *
        * «Ao abrir a OS normalmente a informação que temos é a
        *  quantidade de cada ensaio. Não necessariamente saberemos a
        *  programação de ensaios para cada amostra» (Testesolo,
        *  02/09/2026). Esta tabela é essa informação: quantos ensaios de
        *  cada método foram contratados, quantos já foram programados e
        *  quanto ainda cabe.
        *
        * A lista de itens comerciais continua abaixo, porque é ela que
        * a medição fatura — mas ela deixa de ser a primeira coisa que
        * quem abre a OS enxerga.
        */}
      {/* 268: a faixa do escopo — o mesmo desenho da Programação e do recebimento. */}
      {porMetodo.length > 0 && (
        <div className="rola escopo-metodo">
          <FaixaDoEscopo linhas={porMetodo.map(linhaDoServidor)} />
        </div>
      )}

      {itens.length === 0 ? (
        <Vazio>
          Sem itens de escopo. O escopo vem da proposta comercial e é a
          coluna «prevista» da medição: sem ele o sistema apura quanto foi
          executado e não tem contra o que comparar — nem saldo, nem
          percentual, nem excedente.
          <div className="sub" style={{ marginTop: '.5rem' }}>
            Sem escopo, a OS não programa: é o escopo que libera a
            programação (RN-218). Ele é também o teto — ensaio fora dele é
            recusado, e incluir um depois que a programação começou é
            revisão da OS.
          </div>
        </Vazio>
      ) : (
        <div className="rola">
          <table className="dados">
            <thead>
              <tr>
                <th>Código</th><th>Item</th><th>Unidade</th>
                <th className="num">Previsto</th>
                <th>Vigente desde</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.id}>
                  <td className="mono">{i.codigo}</td>
                  <td>
                    {i.item}
                    {i.observacoes && <div className="sub">{i.observacoes}</div>}
                  </td>
                  <td>{i.unidade}</td>
                  <td className="num mono">{numero(i.quantidade_prevista, 2, true)}</td>
                  {/* 283: de que revisão vem a quantidade de hoje. */}
                  <td className="sub">
                    {Number(i.revisao_inicio ?? 0) === 0 ? 'emissão (rev. 0)' : `rev. ${i.revisao_inicio}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        As revisões, embaixo do escopo e não numa aba própria.
        A pergunta "isto ainda é o que foi contratado?" nasce olhando a
        tabela acima — e uma resposta a dois cliques de distância é uma
        resposta que ninguém procura.
      */}
      {revisoes.length > 0 && (
        <div className="revisoes">
          <h3>Revisões do escopo</h3>
          <ol className="linha-do-tempo">
            {revisoes.map((r) => (
              <li key={r.id}>
                <b>Revisão {String(r.numero)}</b>
                <span className="meta">
                  {dataHora(r.criado_em)}
                  {r.criado_por_nome && ` · ${r.criado_por_nome}`}
                  {r.solicitante && ` · pedido por ${r.solicitante}`}
                </span>
                {/* 283: o que mudou nesta revisão, item por item. */}
                {(r.mudancas ?? []).length > 0 && (
                  <span className="revisao-mudancas">
                    {(r.mudancas ?? []).map((m) => (
                      <span key={m.codigo} className="chip-mudanca mono">{dizMudanca(m)}</span>
                    ))}
                  </span>
                )}
                <p>{r.motivo}</p>
              </li>
            ))}
          </ol>
          <p className="dica">
            Os itens retirados em cada revisão continuam gravados — nada é
            apagado. É o que permite conferir depois o que estava
            contratado quando cada ensaio foi executado.
          </p>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
 * Quantitativos executados — §13.2 e §13.3
 * ══════════════════════════════════════════════════════════════════════
 *
 * O que já foi feito, contra o que foi contratado, item por item.
 *
 * ─── Três colunas, e não uma ─────────────────────────────────────────
 *
 * `Aprovado` é o que a `RN-171` chama de medível, e é o único número
 * que pode entrar num boletim. Mas ele sozinho engana: o ensaio que a
 * bancada terminou ontem e que está esperando verificação já é serviço
 * prestado, e some de uma tela que só mostra o aprovado. Por isso
 * `Em andamento` aparece ao lado — é o décimo segundo número do §13.3,
 * e é ele que responde «o que fecha ainda neste período?».
 *
 * `Cancelado` fecha a conta. Sem ele o item cujo ensaio morreu
 * simplesmente encolhe, e ninguém pergunta por quê.
 *
 * ─── Por que cada linha abre ─────────────────────────────────────────
 *
 * «Ao selecionar um quantitativo, o usuário deverá conseguir abrir os
 *  registros que comprovam sua execução» (§13.5). Cada item abre a
 * lista de ensaios que o compõem, com amostra, método, status e quem
 * aprovou. Um número de medição que não se abre é um número que
 * ninguém consegue contestar — nem defender.
 */

interface ItemQuantitativo {
  item_medicao_id: string;
  item_codigo: string;
  item_descricao: string;
  item_categoria: string | null;
  unidade: string;
  unidade_nome: string;
  quantidade_prevista: string | null;
  qtd_aprovada: string;
  qtd_em_andamento: string;
  qtd_cancelada: string;
  saldo_remanescente: string | null;
  qtd_excedente: string | null;
  percentual_executado: string | null;
  fora_do_escopo: boolean;
  evidencias: Array<{
    ensaio_id: string; quantidade: string; status: string;
    metodo: string; metodo_nome: string;
    amostra: string | null; identificacao: string | null;
    data_execucao: string | null; data_aprovacao: string | null;
    aprovado_por: string | null;
  }>;
}

interface Quantitativos {
  itens: ItemQuantitativo[];
  resumo: {
    itens: number; previstos: number; fora_do_escopo: number;
    concluidos: number; com_saldo: number; excedentes: number;
    em_andamento: number;
  };
}

function Quantitativos(
  { osId, podeMedir, aoMedir }:
  { osId: string; podeMedir: boolean; aoMedir(): void },
) {
  const [d, setD] = useState<Quantitativos | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [aberto, setAberto] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api<Quantitativos>(`/os/${osId}/quantitativos`)
      .then((r) => { if (vivo) setD(r); })
      .catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [osId]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Apurando os quantitativos" />;

  const { resumo } = d;

  return (
    <div className="cartao quantitativos">
      <div className="cartao-cabeca">
        <h2>Quantitativos executados</h2>
        <span className="sub cresce">
          {resumo.itens} item(ns)
          {resumo.concluidos > 0 && ` · ${resumo.concluidos} concluído(s)`}
          {resumo.com_saldo > 0 && ` · ${resumo.com_saldo} com saldo`}
          {resumo.excedentes > 0 && ` · ${resumo.excedentes} excedente(s)`}
        </span>
        {/*
          * O botão fica AQUI, e não numa tela de medições.
          *
          * Abrir um boletim é a consequência de olhar esta tabela e ver
          * que há o que medir. Uma tela de medições que começa com «nova
          * medição» obrigaria a escolher obra e período no escuro, sem
          * ver o que existe para medir.
          */}
        {podeMedir && Number(resumo.itens) > 0 && (
          <button type="button" className="botao secundario" onClick={aoMedir}>
            Abrir boletim de medição
          </button>
        )}
      </div>

      <p className="sub">
        Apurado dos registros operacionais (§13.2): cada quantidade sai
        dos ensaios desta OS, e cada linha abre a lista deles.
      </p>

      {resumo.fora_do_escopo > 0 && (
        <Aviso tipo="atencao" titulo="Serviço executado fora do escopo">
          {resumo.fora_do_escopo} item(ns) foram executados sem estar no
          escopo contratado desta OS. Não é erro do sistema — é o que
          aconteceu. Antes de medir, ou o escopo recebe uma revisão, ou o
          excedente precisa de autorização (§13.3).
        </Aviso>
      )}

      {d.itens.length === 0 ? (
        <Vazio>
          Nada apurado ainda. Assim que o primeiro ensaio desta OS for
          aprovado, ele aparece aqui — com o caminho de volta até a
          amostra que o originou.
        </Vazio>
      ) : (
        <div className="rola">
          <table className="dados">
            <thead>
              <tr>
                <th>Código</th><th>Item</th><th>Unidade</th>
                <th className="num">Previsto</th>
                <th className="num">Aprovado</th>
                <th className="num">Em andamento</th>
                <th className="num">Saldo</th>
                <th className="num">%</th>
              </tr>
            </thead>
            <tbody>
              {d.itens.map((i) => {
                const eu = aberto === i.item_medicao_id;
                const excedeu = i.qtd_excedente != null
                  && Number(i.qtd_excedente) > 0;
                return [
                  <tr key={i.item_medicao_id}
                    className={`abrivel${eu ? ' aberta' : ''}`}
                    onClick={() => setAberto(eu ? null : i.item_medicao_id)}>
                    <td className="mono">
                      {i.item_codigo}
                      {i.fora_do_escopo && (
                        <> <Selo tom="atencao">Fora do escopo</Selo></>
                      )}
                    </td>
                    <td>
                      {i.item_descricao}
                      {i.item_categoria && (
                        <div className="sub">{i.item_categoria}</div>
                      )}
                    </td>
                    <td>{i.unidade_nome}</td>
                    {/* CA-29: célula vazia diz por que está vazia. */}
                    <td className="num mono">
                      {i.quantidade_prevista == null
                        ? <span className="nota" title="Item não previsto no escopo contratado desta OS">não previsto</span>
                        : numero(i.quantidade_prevista, 2, true)}
                    </td>
                    <td className="num mono">{numero(i.qtd_aprovada, 2, true)}</td>
                    <td className="num mono">
                      {Number(i.qtd_em_andamento) > 0
                        ? numero(i.qtd_em_andamento, 2, true)
                        : <span className="nota">—</span>}
                    </td>
                    <td className="num mono">
                      {excedeu
                        ? <Selo tom="atencao">
                            +{numero(i.qtd_excedente, 2, true)}
                          </Selo>
                        : i.saldo_remanescente == null
                          ? <span className="nota">—</span>
                          : numero(i.saldo_remanescente, 2, true)}
                    </td>
                    <td className="num mono">
                      {i.percentual_executado == null
                        ? <span className="nota">—</span>
                        : `${numero(i.percentual_executado, 0, true)} %`}
                    </td>
                  </tr>,
                  eu ? (
                    <tr key={`${i.item_medicao_id}-ev`} className="detalhe">
                      <td colSpan={8}>
                        <h3>De onde vem este número</h3>
                        {i.evidencias.length === 0 ? (
                          <p className="nota">
                            Nenhum ensaio registrado — este item está no
                            escopo e ainda não começou.
                          </p>
                        ) : (
                          <table className="dados compacta">
                            <thead>
                              <tr>
                                <th>Amostra</th><th>Método</th><th>Situação</th>
                                <th className="num">Vale</th>
                                <th>Aprovado em</th><th>Por</th>
                              </tr>
                            </thead>
                            <tbody>
                              {i.evidencias.map((e) => (
                                <tr key={e.ensaio_id}>
                                  <td>
                                    {/* 303 (RN-248): o nome do cliente, o
                                        código embaixo (dentro da OS). */}
                                    <Link to={`/ensaio/${e.ensaio_id}`}>
                                      {e.amostra || e.identificacao
                                        ? <NomeDaAmostra amostra={{
                                          codigo: e.amostra, identificacao: e.identificacao }} />
                                        : '—'}
                                    </Link>
                                  </td>
                                  <td>
                                    <span className="mono">{e.metodo}</span>
                                  </td>
                                  <td>
                                    <SeloDoEnsaio status={e.status} />
                                  </td>
                                  <td className="num mono">
                                    {numero(e.quantidade, 2, true)}
                                  </td>
                                  <td>{e.data_aprovacao
                                    ? data(e.data_aprovacao)
                                    : <span className="nota">—</span>}</td>
                                  <td>{e.aprovado_por
                                    ?? <span className="nota">—</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="dica">
        Ainda não é medição: aqui não se fecha período nem se emite
        boletim. O que esta tabela garante é que, quando o boletim
        existir, cada número dele já tenha de onde vir.
      </p>
    </div>
  );
}

/**
 * O histórico dos três status, em ordem de acontecimento.
 *
 * Vem de `os_status_historico`, que é escrito por gatilho. A diferença
 * entre isto e um `log` da aplicação é que aqui não existe caminho por
 * fora: um `UPDATE` feito por script de correção também cai aqui.
 */
function Historico({ linhas }: { linhas: Evento[] | null }) {
  if (linhas === null) return <Carregando texto="Carregando o histórico" />;

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Histórico</h2>
        <span className="sub">{linhas.length} mudança(s)</span>
      </div>

      {linhas.length === 0 ? (
        <Vazio>
          Nenhuma mudança de situação ainda. A OS está como foi aberta.
        </Vazio>
      ) : (
        <div className="rola">
          <table className="dados">
            <thead>
              <tr>
                <th>Quando</th><th>O quê</th><th>Mudança</th><th>Quem</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((e, i) => (
                <tr key={`${e.datahora}-${e.campo}-${i}`}>
                  <td>{dataHora(e.datahora)}</td>
                  <td>{e.campo === 'arquivo' ? 'Arquivamento' : TITULO_STATUS[e.campo as CampoStatus] ?? e.campo}</td>
                  <td>
                    {e.de ? `${rotulo(e.de)} → ` : ''}<b>{rotulo(e.para)}</b>
                    {e.motivo && <div className="sub">{e.motivo}</div>}
                  </td>
                  <td>
                    {e.usuario_nome ?? e.usuario_email
                      ?? <span className="nada">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="rodape-regra">
        Histórico não se apaga nem se corrige. Uma mudança errada se conserta
        com a mudança seguinte, e as duas ficam.
      </p>
    </div>
  );
}

// ── A contagem que sustenta a situação técnica ───────────────────────

/**
 * Os números por trás do estado.
 *
 * "Ensaios em andamento" sem dizer quantos de quantos manda o técnico
 * procurar em outra tela — e ele vai, toda vez. A linha aqui embaixo é
 * curta de propósito: ela responde "quanto falta?" sem virar tabela.
 *
 * Some quando não há ensaio nenhum: "0 de 0" não informa nada, e a OS em
 * rascunho já diz o que precisa ser dito.
 */


/**
 * A ordem de serviço em papel.
 *
 * Dois caminhos, e é de propósito: «Conferir» abre o mesmo HTML que o PDF
 * imprime, e «PDF» baixa o arquivo. Quem está no meio da programação
 * confere sem gerar arquivo nenhum; quem vai levar o papel para a bancada
 * baixa.
 *
 * Não há botão de «emitir»: a OS não gera revisão de documento ao ser
 * impressa. A revisão que sai no papel é a REVISÃO DA OS, e ela só sobe
 * quando alguém inclui ou exclui serviço.
 */
/**
 * As ações secundárias da OS — o conteúdo do menu «⋯» do cabeçalho (267).
 * As mesmas em todas as abas, inclusive a Programação.
 */
export function AcoesDaOs({ os }: { os: any }) {
  const { pode } = useSessao();
  return (
    <>
      {/* Furos e diários saíram da ficha (204): a porta é esta. Desde a H1
          (onda 295) é uma tela do Campo, e quem não tem o Campo não vê o link
          — ele levaria a «Você não tem acesso ao Campo» (spec 03 §10). */}
      {pode('campo.ver') && (
        <Link className="item-menu" to={`/os/${os.id}/campo`}>
          Campo · furos e diários
        </Link>
      )}
      <ImprimirOs os={os} />
      <ExcluirDeVez tipo="os" id={os.id} />
    </>
  );
}

function ImprimirOs({ os }: { os: any }) {
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  async function conferir() {
    setErro(null); setOcupado('conferir');
    try {
      setHtml(await api<string>(`/os/${os.id}/documento`, { cru: true }));
    } catch (e) { setErro(e); } finally { setOcupado(null); }
  }

  async function baixarPdf() {
    setErro(null); setOcupado('pdf');
    try {
      const blob = await baixarBlob(`/os/${os.id}/documento.pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `OS-${os.numero}-R${String(os.revisao ?? 0).padStart(2, '0')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      /* Sem o revoke, cada emissão deixa um blob preso na memória da aba —
         e esta tela é reaberta o dia inteiro. */
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { setErro(e); } finally { setOcupado(null); }
  }

  return (
    <>
      <button type="button" className="item-menu" disabled={!!ocupado}
        onClick={conferir}>
        {ocupado === 'conferir' ? 'Abrindo…' : 'Conferir OS'}
      </button>
      <button type="button" className="item-menu" disabled={!!ocupado}
        onClick={baixarPdf}>
        {ocupado === 'pdf' ? 'Gerando…' : 'PDF da OS'}
      </button>
      {/* 267: o erro e a prévia vão para o `body` — o menu «⋯» fecha. */}
      {erro && createPortal(
        <Dialogo aberto titulo="Não deu para abrir a OS em papel"
          aoFechar={() => setErro(null)}>
          <ErroDaApi erro={erro} />
        </Dialogo>, document.body)}
      {html && createPortal(
        <Dialogo aberto titulo={`Ordem de serviço ${os.numero}`}
          aoFechar={() => setHtml(null)}>
          {/*
            * A folha A4, como nas outras duas prévias. Um quadro de
            * largura total mostra um documento que não é o que sai na
            * impressora — colunas largas demais e quebras em outros
            * lugares (Testesolo, 02/09/2026).
            */}
          <FolhaA4 titulo={`Ordem de serviço ${os.numero}`} corpo={html}
            isolado altura="65vh" />
        </Dialogo>, document.body)}
    </>
  );
}
